import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';

export default class VariantPicker extends Component {
  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('change', this.onVariantChange.bind(this));

    this.variantData = this.getVariantData();

    this.updateSelectedState();
  }

  getVariantData() {
    const script = this.querySelector('[data-variant-json]');

    if (!script) {
      console.error('Variant JSON script missing');
      return [];
    }

    try {
      return JSON.parse(script.textContent);
    } catch (error) {
      console.error('Variant JSON parse failed', error);
      return [];
    }
  }

  onVariantChange(event) {
    const target = event.target;

    if (
      !(target instanceof HTMLInputElement) &&
      !(target instanceof HTMLSelectElement)
    ) {
      return;
    }

    this.updateSelectedState();

    const variant = this.findCurrentVariant();

    if (!variant) {
      console.warn('No matching variant found');
      return;
    }

    this.dataset.selectedVariantId = variant.id;

    this.updateUrl(variant.id);
    this.updateProductForms(variant);
    this.updateAddToCartButtons(variant);
    this.updateProductMediaImages(variant);

    this.dispatchEvent(
      new VariantSelectedEvent({
        id: variant.id,
        variant,
      })
    );

    this.dispatchEvent(
      new VariantUpdateEvent(
        variant,
        variant.id,
        {
          html: document,
          productId: String(this.dataset.productId || ''),
          newProduct: null,
        }
      )
    );
  }

  getSelectedOptions() {
    const selected = [];

    const fieldsets = this.querySelectorAll('fieldset');

    fieldsets.forEach((fieldset) => {
      const checked = fieldset.querySelector('input:checked');

      if (checked instanceof HTMLInputElement) {
        selected.push(checked.value);
      }
    });

    const selects = this.querySelectorAll('select');

    selects.forEach((select) => {
      selected.push(select.value);
    });

    return selected;
  }

  findCurrentVariant() {
    const selectedOptions = this.getSelectedOptions();

    return this.variantData.find((variant) => {
      return variant.options.every((option, index) => {
        return option === selectedOptions[index];
      });
    });
  }

  updateUrl(variantId) {
    if (this.dataset.templateProductMatch !== 'true') return;

    const url = new URL(window.location.href);

    url.searchParams.set('variant', variantId);

    window.history.replaceState({}, '', url.toString());
  }

  updateProductForms(variant) {
    const productId = String(this.dataset.productId || '');

    const productForms = document.querySelectorAll(
      `product-form-component[data-product-id="${productId}"]`
    );

    productForms.forEach((formComponent) => {
      const variantInput = formComponent.querySelector('input[name="id"]');

      if (!(variantInput instanceof HTMLInputElement)) return;

      variantInput.value = variant.id;
      variantInput.disabled = variant.available === false;
    });
  }

  updateAddToCartButtons(variant) {
    const productId = String(this.dataset.productId || '');

    const productForms = document.querySelectorAll(
      `product-form-component[data-product-id="${productId}"]`
    );

    productForms.forEach((formComponent) => {
      const submitButtons = formComponent.querySelectorAll(
        'button[type="submit"], button[name="add"], add-to-cart-component button'
      );

      submitButtons.forEach((button) => {
        if (!(button instanceof HTMLButtonElement)) return;

        button.disabled = variant.available === false;
        button.setAttribute(
          'aria-disabled',
          variant.available === false ? 'true' : 'false'
        );
      });
    });
  }

  updateProductMediaImages(variant) {
    if (!variant.featured_media?.preview_image?.src) return;

    const mediaGallery = document.querySelector('media-gallery');

    if (!mediaGallery) return;

    const newSrc = variant.featured_media.preview_image.src;
    const newAlt = variant.featured_media.alt || variant.title || '';

    const visibleImages = mediaGallery.querySelectorAll(
      '.product-media img, slideshow-slide[aria-hidden="false"] img'
    );

    visibleImages.forEach((image) => {
      if (!(image instanceof HTMLImageElement)) return;

      image.src = newSrc;
      image.srcset = '';
      image.alt = newAlt;
    });
  }

  updateSelectedState() {
    const fieldsets = this.querySelectorAll('fieldset');

    fieldsets.forEach((fieldset) => {
      const radios = fieldset.querySelectorAll('input[type="radio"]');

      radios.forEach((radio) => {
        if (!(radio instanceof HTMLInputElement)) return;

        radio.dataset.currentChecked = radio.checked ? 'true' : 'false';
        radio.dataset.previousChecked = radio.checked ? 'false' : 'false';
      });
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}